<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { displayReferenceDate, maskReferenceDate } from './postSaleDate';
import SalesPropertyReferenceField from './SalesPropertyReferenceField.vue';

const props = defineProps({
  profile: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
  originOptions: { type: Array, default: () => [] },
  contactOptions: { type: Array, default: () => [] },
  catalogLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['update']);
const { t } = useI18n();
const origin = computed(() => props.profile.origin || {});
const update = (field, event) => {
  const value =
    field === 'contacted_on'
      ? maskReferenceDate(event.target.value)
      : event.target.value;
  emit('update', 'origin', field, value);
};
const contactedOn = computed(() =>
  displayReferenceDate(origin.value.contacted_on)
);
const fieldFilled = field => Boolean(String(origin.value[field] || '').trim());
const referenceControlClasses = ({ filled, error }) => {
  const classes = [
    'reset-base h-8 w-full border-0 border-b bg-transparent px-0 pr-7 text-base outline-none placeholder:text-[#A7B7BE] focus:ring-0',
  ];

  if (error) {
    classes.push('border-[#D64545] text-[#D64545]');
  } else if (filled) {
    classes.push('border-[#009F94] font-bold text-[#009F94]');
  } else {
    classes.push('border-[#A7B7BE] text-[#7F8C93]');
  }

  return classes;
};
</script>

<template>
  <section
    data-test="sales-party-origin-section"
    class="overflow-x-hidden rounded-[10px] bg-white p-8 font-roboto shadow-sm max-[700px]:p-0"
    :aria-busy="catalogLoading"
  >
    <header class="mb-6 max-[700px]:mb-11">
      <h2
        data-test="sales-party-origin-title"
        class="font-lato text-[32px] font-bold leading-[39px] text-[#464F53] max-[700px]:text-[24px] max-[700px]:leading-[29px]"
      >
        {{ t('SALES_PARTIES.ORIGIN.TITLE') }}
      </h2>
    </header>
    <div
      data-test="sales-party-origin-fields"
      class="grid gap-8 min-[701px]:grid-cols-3 min-[701px]:gap-x-[14px]"
    >
      <div data-origin-field="channel" class="min-w-0 overflow-hidden">
        <SalesPropertyReferenceField
          :filled="fieldFilled('channel')"
          :label="t('SALES_PARTIES.ORIGIN.CHANNEL')"
          :error="errors.channel"
          required
          indicator="select"
          test-id="sales-party-origin-channel"
        >
          <select
            :value="origin.channel || '0'"
            :class="
              referenceControlClasses({
                error: errors.channel,
                filled: fieldFilled('channel'),
              })
            "
            class="appearance-none"
            @change="update('channel', $event)"
          >
            <option value="0">
              {{ t('SALES_PARTIES.ORIGIN.CHANNEL_PLACEHOLDER') }}
            </option>
            <option
              v-for="option in originOptions"
              :key="option.id"
              :value="option.id"
            >
              {{ option.name }}
            </option>
          </select>
        </SalesPropertyReferenceField>
      </div>
      <div data-origin-field="contact_method" class="min-w-0 overflow-hidden">
        <SalesPropertyReferenceField
          :filled="fieldFilled('contact_method')"
          :label="t('SALES_PARTIES.ORIGIN.CONTACT_METHOD')"
          :error="errors.contact_method"
          required
          indicator="select"
          test-id="sales-party-origin-contact-method"
        >
          <select
            :value="origin.contact_method || '0'"
            :class="
              referenceControlClasses({
                error: errors.contact_method,
                filled: fieldFilled('contact_method'),
              })
            "
            class="appearance-none"
            @change="update('contact_method', $event)"
          >
            <option value="0">
              {{ t('SALES_PARTIES.ORIGIN.CONTACT_METHOD_PLACEHOLDER') }}
            </option>
            <option
              v-for="option in contactOptions"
              :key="option.id"
              :value="option.id"
            >
              {{ option.name }}
            </option>
          </select>
        </SalesPropertyReferenceField>
      </div>
      <div data-origin-field="contacted_on" class="min-w-0 overflow-hidden">
        <SalesPropertyReferenceField
          :filled="Boolean(contactedOn)"
          :label="t('SALES_PARTIES.ORIGIN.CONTACTED_ON')"
          :error="errors.contacted_on"
          required
          test-id="sales-party-origin-contacted-on"
        >
          <input
            :value="contactedOn"
            type="text"
            inputmode="numeric"
            maxlength="10"
            :placeholder="t('SALES_PARTIES.ORIGIN.CONTACTED_ON_PLACEHOLDER')"
            :class="
              referenceControlClasses({
                error: errors.contacted_on,
                filled: Boolean(contactedOn),
              })
            "
            @input="update('contacted_on', $event)"
          />
        </SalesPropertyReferenceField>
      </div>
    </div>
  </section>
</template>
